import {
    Container, Grid, IconButton
} from "@material-ui/core";
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import {
    Delete as DeleteIcon
} from "@material-ui/icons";
import clsx from "clsx";
import MUIDataTable from "mui-datatables";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import SelectSupplier from "../../../components/Form/SelectSupplier";
import AlertDialog from "../../../components/ui/AlertDialog";
import ButtonUI from "../../../components/ui/ButtonUI";
import LoadingUI from "../../../components/ui/LoadingUI";
import PageTitle from "../../../components/ui/PageTitleUI";
import { useAuth } from "../../../hooks/auth";
import { ShippingRequestSupplierPost, ShippingRequestSupplierSearch } from "../../../interfaces/ShippingRequest";
import ShippingRequestApi from "../../../services/ShippingRequestApi";
import getDefaultGridOptions from "../../../utils/componentUtils";
import utilsDate from "../../../utils/Date";
import permissions from "../../../utils/Permissions";
import { useStyles } from "./Style";

interface IFormInput {
    supplierId: number;
    supplierCorporateName: string;
    createdBy: string;
    createdAt: Date;
    supplier: any;
}

const ShippingRequestManageSuppliers: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const [shippingRequestSuppliers, setShippingRequestSuppliers] =
        useState<any>();
    const [loading, setLoading] = useState(false);
    const { user, hasPermission } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("ShippingRequestManageSuppliers");
    const { t: _t } = useTranslation("General");
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const { control, handleSubmit } = useForm<IFormInput>({
        defaultValues: {
            supplierId: 0,
            supplierCorporateName: "",
            createdBy: "",
            supplier: null,
            createdAt: new Date()
        },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.SHIPPING_REQUEST.Supplier))
            history.replace('NoPermission');
    }, [history, user])

    const searchShippingRequestSuppliers = () => {
        setLoading(true);

        let filter: ShippingRequestSupplierSearch = {
            companyId: +user.company.companyID,
            plantId: Number(user.company.plantID),
            pageSize: rowsPerPage,
            pageIndex: page,
            sort: "CreatedAt",
            sortDir: "desc",
        };

        ShippingRequestApi.searchSupplier(filter)
            .then((result) => {
                if (result && result.items) {
                    result.items.forEach((supplier: any) => {
                        supplier.supplierCorporateName = `${supplier.supplierId} - ${supplier.supplierCorporateName}`
                    });

                    setShippingRequestSuppliers(result);
                    setPage(result.pageIndex);
                } else {
                    setShippingRequestSuppliers([]);
                    setPage(0);
                }
            })
            .catch(() => {
                setShippingRequestSuppliers([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setShippingRequestSuppliers([]);
        searchShippingRequestSuppliers();
    }, [user, page, rowsPerPage]);

    const onSubmit = (data: any) => {
        handleSubmitPost(data);
    };

    const tableHeadAlignCenter = clsx({
        [classes.tableHeadCenter]: true,
    });

    const handleSubmitPost = (data: IFormInput) => {
        if(!data.supplier) {
            enqueueSnackbar(t("warningSelectSupplier"), {
                variant: "warning",
            });
            return;
        }

        setLoading(true);

        let shippingRequestPost: ShippingRequestSupplierPost = {
            supplierId: data.supplier?.code,
            companyId: data.supplier?.companyId,
            supplierCorporateName: data.supplier?.name,
            plantId: Number(user.company.plantID),
        };


        ShippingRequestApi.postSupplier(shippingRequestPost)
            .then((result) => {
                enqueueSnackbar(t("returnSubmitFormSuccess"), {
                    variant: "success",
                });
            })
            .finally(() => {
                searchShippingRequestSuppliers();
            });
    };

    const columns = [
        {
            name: "id",
            options: {
                display: false,
            },
        },
        {
            name: "supplierId",
            options: {
                display: false,
            },
        },
        {
            name: "supplierCorporateName",
            label: String(t("supplier")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: tableHeadAlignCenter,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "createdBy",
            label: String(_t("createdBy")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: tableHeadAlignCenter,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "createdAt",
            label: String(_t("createdAt")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    className: tableHeadAlignCenter,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => {
                    return utilsDate.DateTimeShort(value);
                },
            },
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right",
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => renderButtons(tableMeta.rowData[1]),
            },
        },
    ];

    const renderButtons = (supplierId: any) => {
        return (
            <AlertDialog
                dialogTitle={_t("dialogDeleteTitle")}
                dialogContentText={t("dialogDeleteContentText")}
                cancelTextButton={_t("cancel")}
                confirmTextButton={_t("confirm")}
                confirmDialogFunction={() => {
                    handleDeleteItem(supplierId);
                }}
                Button={
                    <IconButton size="small" edge="end">
                        <DeleteIcon />
                    </IconButton>
                }
            />
        );
    };

    function handleDeleteItem(supplierId: number) {
        setLoading(true);
        const companyId = +user.company.companyID;

        ShippingRequestApi.deleteSupplier(supplierId, companyId)
            .then((result) => {
                enqueueSnackbar(t("returnSubmitDeleteFormSuccess"), {
                    variant: "success",
                });
            })
            .catch(() => {
                enqueueSnackbar(t("returnSubmitDeleteFormError"), {
                    variant: "success",
                });
            })
            .finally(() => {
                searchShippingRequestSuppliers();
            });
    }

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {},
            },
        },
    });
    return (
        <Container maxWidth={false} component="main">
            <PageTitle title={t("shippingRequestManageSupplier")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid
                            container
                            spacing={1}
                            style={{ marginBottom: 10 }}
                        >
                            <Grid item xs={4}>
                                <SelectSupplier
                                    label={t("supplier")}
                                    name="supplier"
                                    control={control}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item>
                                <ButtonUI
                                    color="primary"
                                    variant="contained"
                                    type="submit"
                                    className="sizeSmall textSizeSmall"
                                    style={{
                                        maxHeight: "38px",
                                        minHeight: "38px",
                                        marginTop: 15,
                                        marginLeft: "10px",
                                    }}
                                >
                                    Incluir
                                </ButtonUI>
                            </Grid>
                        </Grid>
                    </form>

                    {loading ? (
                        <LoadingUI />
                    ) : (
                        <MuiThemeProvider theme={myTheme}>
                            <MUIDataTable
                                title=""
                                data={
                                    shippingRequestSuppliers &&
                                    shippingRequestSuppliers?.items
                                }
                                columns={columns}
                                options={{
                                    ...getDefaultGridOptions(_t),
                                    serverSide: false,
                                    pagination: false,
                                    sort: true,
                                    selectToolbarPlacement: "none",
                                    filter: false,
                                    search: false,
                                    download: false,
                                    print: false,
                                    viewColumns: false,
                                    selectableRows: "none",
                                }}
                            />
                        </MuiThemeProvider>
                    )}
                </div>
            </div>
        </Container>
    );
};
export default ShippingRequestManageSuppliers;
